import {
  BucketSummaryEnum,
  ReportJobStatus,
  SingleValueCardColor,
  SingleValueCardType,
} from "constants/index";

export type ColumnPeriodT = {
  name: string;
  period: [string, string];
};

export interface PerformanceComparisonTableProps {
  dateRange: [string | null, string | null];
  onColumnPeriodsChange: (periods: ColumnPeriodT[]) => void;
}

export interface PerformanceColumnSelectorModalProps {
  open: boolean;
  onClose: () => void;
  columnPeriods: ColumnPeriodT[];
  onChange: (columns: ColumnPeriodT[]) => void;
  dateRange: [string | null, string | null];
}

export interface StatisticState {
  dateRange: [string | null, string | null];
}

export interface IncomeSummaryProps {
  columnPeriods: ColumnPeriodT[];
}

export interface OtherStatisticProps {
  dateRange: [string | null, string | null];
  columnPeriods: ColumnPeriodT[];
}

// Report related interfaces
export interface ReportValue {
  date: string;
  beginning_value: number;
  value_change: number;
  cashflows: number;
  dividends: number;
  closing_price: number;
  nav_price: number;
  unit_balance: number;
  factor: number;
  stock_factor: number;
  time_weighted_return: number;
  time_weighted_return_stock: number;
}

export interface IncomeSummaryItem {
  name: string;
  value: ReportValue;
}

export interface GraphData {
  date: string;
  stock_value: number;
  option_value: number;
}

export interface ReportResponse {
  status: string;
  data: {
    report_data: string;
    income_summary: IncomeSummaryItem[];
    graph_data: GraphData[];
  };
  message: string;
}

export interface ReportParams {
  account_ids: number[];
  strategy_ids: number[];
  periods: {
    name: string;
    period: [string, string];
  }[];
}

export interface NewReportRequest {
  orion_account_id: number;
  asset_id: number;
  option_type: OptionTypeEnum;
  start_date: string;
  end_date: string;
  global_cumulative_period?: [string, string];
  report_type: ReportType;
  exclude_transactions?: OrionTransaction["id"][];
}

/**
 * Batch STRATEGY report over an account's persisted Bucket 1/2 members.
 * Buckets are CP-only — each pair runs one combined call+put report — so
 * no option_type is sent. This phase returns only the combined excel (one
 * sheet per pair) + per-pair statuses.
 */
export interface BucketReportRequest {
  orion_account_id: number;
  start_date: string;
  end_date: string;
  // Pair buckets (1/2) of orion_account_id to include.
  buckets?: number[];
  // The exact Bucket 3 to add as the account-level Bucket3 sheet — may
  // belong to a different account; the backend derives it from the bucket.
  bucket_3_id?: number;
  // Tax percentages from the report settings: the federal rate is a
  // browser-local setting, the state rate comes from the client's TaxRate
  // mapping (null when no state is selected). They drive the Keep card's
  // tax savings.
  federal_tax_rate?: number;
  state_tax_rate?: number | null;
}

export enum BucketReportStatusEnum {
  SUCCESS = "success",
  ERROR = "error",
}

export interface BucketPairReportStatus {
  bucket: number;
  // null = the account-level Bucket 3 sheet (one job for the whole bucket,
  // no single pair).
  symbol: string | null;
  // null = a portfolio-only report (the pair has no option history, which
  // is expected — it still gets its sheet, on portfolio values alone).
  option_type: string | null;
  status: BucketReportStatusEnum;
  message: string | null;
}

/**
 * One day of a scope's chart series: summed cumulative_beginning_value for
 * Bucket 1/2, the account's Orion market value for Bucket 3.
 */
export interface BucketDailyValue {
  date: string;
  // null = Orion couldn't price the account that day (Bucket 3 only).
  value: number | null;
}

/**
 * card_1/2 are the scope's starting and ending value, card_3 their
 * difference, card_5 income received, card_6 the account's margin
 * interest. card_4 is reserved (0 for now).
 */
export interface BucketSingleInfoCards {
  card_1: number;
  card_2: number;
  card_3: number;
  card_4: number;
  card_5: number;
  card_6: number;
}

export interface BucketTotalsEntry {
  daily_values: BucketDailyValue[];
  single_info_cards: BucketSingleInfoCards;
}

/** The last-day position sums only Bucket 3 carries. */
export interface Bucket3Totals extends BucketTotalsEntry {
  market_value: number | null;
  cost_basis: number | null;
  unrealized_gain_loss: number | null;
  positions_as_of: string | null;
  end_date: string;
}

/**
 * Keyed as the summary switch selects. A summary with no successful data
 * (an empty bucket, a not-requested or failed Bucket 3) is absent.
 */
export type BucketTotals = {
  [BucketSummaryEnum.BUCKET_1]?: BucketTotalsEntry;
  [BucketSummaryEnum.BUCKET_2]?: BucketTotalsEntry;
  [BucketSummaryEnum.BUCKET_3]?: Bucket3Totals;
  [BucketSummaryEnum.LONG_VALUE]?: BucketTotalsEntry;
  [BucketSummaryEnum.NET_VALUE]?: BucketTotalsEntry;
};

/** One "Keep" card option: a single asset's values, or their sum. */
export interface KeepCardValues {
  shares_start: number;
  shares_end: number;
  value_start: number;
  value_end: number;
  dividends: number;
  participation_rate: number;
  basis: number;
  tax_savings: number;
}

/** The "Keep" themed card, from Bucket 1 only — it does not follow the page's bucket switch. */
export interface KeepThemedCardValues {
  total: KeepCardValues;
  assets: Record<string, KeepCardValues>;
}

/**
 * The "Protect" themed card, from Bucket 1 only. collar_pl is the
 * period's total option cashflows; max_stock_drop holds each member's
 * max drop percent, keyed by symbol, and main_symbol is the asset the
 * dropdown should default to (null when no main member has data);
 * oea_drop is the whole portfolio's max drop percent — the summed
 * Bucket 1/2/3 daily values (null when no bucket has values);
 * available_margin_balance is reserved (0 for now).
 */
export interface ProtectThemedCardValues {
  max_stock_drop: Record<string, number>;
  main_symbol: string | null;
  oea_drop: number | null;
  collar_pl: number;
  available_margin_balance: number;
}

/**
 * The "Harvest" themed card, from Bucket 3 only: unrealized_gain_loss is
 * the account's summed position unrealized gain/loss on the period's last
 * snapshot — the Bucket3 sheet's Unrealized G/L row (null when the account
 * has no snapshot, so the stored value stays).
 */
export interface HarvestThemedCardValues {
  unrealized_gain_loss: number | null;
}

export interface MaxDropLimits {
  maxDrop: number;
  negativeLimit: number;
}

/** diversify lands as a sibling in a follow-up. */
export interface BucketThemedCards {
  keep?: KeepThemedCardValues;
  protect?: ProtectThemedCardValues;
  harvest?: HarvestThemedCardValues;
}

export interface BucketReportResponse {
  excel_url: string;
  reports: BucketPairReportStatus[];
  // These are optional only because a job result cached before the field
  // shipped won't carry it; fresh responses always do.
  bucket_totals?: BucketTotals;
  themed_cards?: BucketThemedCards;
}

export interface ReportJob<TResult> {
  key: string;
  status: ReportJobStatus;
  result: TResult | null;
  status_message: string | null;
  // Omitted by the backend on a NOT_FOUND status response.
  started_at?: string;
}

export interface NewReportSingleInfoCards {
  card_1: number;
  card_2: number;
  card_3: number;
  card_4: number;
  card_5: number;
  card_6: number;
}

export interface NewReportThemedCards {
  keep_your_shares: {
    starting_shares: number;
    current_shares: number;
    stock_price_start: number;
    stock_price_now: number;
    stock_only_return: number;
    upside_participation: number;
  };
  generate_income: {
    option_income_period: number;
    dividend_income_period: number;
    cumulative_income: number;
    avg_annualized_yield: number;
    current_option_buyback: number;
    net_option_income: number;
  };
  harvest_tax_benefits: {
    net_realized_tlh_losses: number;
    losses_used: number;
    losses_remaining: number;
    shares_sold: number;
    sellable_today: number;
  };
}

/** Strategy report themed sections; mirrors keys returned by the new-report API for report_type strategy. */
export interface NewReportStrategyThemedCards {
  keep: {
    shares_start: number;
    shares_end: number;
    value_start: number;
    value_end: number;
    dividends: number;
    participation_rate: number;
    basis: number;
    tax_savings: number;
  };
  protect: {
    max_stock_drop: number;
    collar_pl: number;
    available_margin_balance: number;
  };
  diversify: {
    overall_diversified_performance: number;
    tax_loss_harvest_sleeve: number;
    beginning_concentration: number;
    current_concentration: number;
    holdings: number;
  };
  harvest: {
    realized_losses: number;
    losses_used: number;
    pool_remaining: number;
    unrealized_gains: number;
    shares_sold: number;
    sellable_today: number;
  };
}

export interface NewReportResponse {
  status: "success";
  data: {
    single_info_cards: NewReportSingleInfoCards;
    graph_or_table_data: ReportChartDataPoint[];
    themed_cards?: NewReportThemedCards | NewReportStrategyThemedCards;
    excel_url?: string;
  };
}

/** PDF report request: matches backend PDFReportSerializer */
export type ReportType = "stock" | "strategy";

export interface SelectedAsset {
  id: number;
  symbol: string;
  name: string;
}

export interface PDFReportSettingsInfo {
  client_name: string;
  client_id?: number | null;
  account_id?: number | null;
  account: string;
  period: [string, string];
  global_cumulative_period?: [string, string];
  symbol: string;
  strategy_id?: number | null;
  orion_account_id?: number | null;
  // The exact Bucket 3 the report should use — a client can have several
  // accounts with a Bucket 3 each (or none), and it may belong to a
  // different account than the Bucket 1/2 one.
  bucket_3_id?: number | null;
}

export interface PDFReportTableColumnRow {
  id: number;
  Period: string;
  "Stock Price": number;
  "Option Income": number;
  "Dividend Income": number;
  "Total Income": number;
  Comment: string;
}

export interface ReportChartDataPoint {
  date: string;
  option_total: number;
  stock_total: number;
}

export enum PDFReportValueType {
  NUMBER = "number",
  CURRENCY = "currency",
  PERCENTAGE = "percentage",
  SHARES = "shares",
}

export enum PDFReportChartType {
  BAR = "bar",
  DONUT = "donut",
}

export interface PDFReportClientInfoItem {
  label: string;
  value: string | number;
  type: PDFReportValueType;
  description: string;
}

export interface PDFReportValueItem {
  label: string;
  value: number;
  type: PDFReportValueType;
}

export interface PDFReportAddictionalComponent {
  type: PDFReportChartType;
  title: string;
  limit: number;
  negativeLimit?: number;
  data: PDFReportValueItem[];
}

export interface PDFReportThemeInfoItem {
  label: string;
  description: string;
  values: PDFReportValueItem[];
  addictional_component?: PDFReportAddictionalComponent | null;
}

export interface PDFReportRequest {
  type: ReportType;
  settings_info?: PDFReportSettingsInfo;
  table_columns?: PDFReportTableColumnRow[];
  chart_data?: ReportChartDataPoint[];
  client_info: PDFReportClientInfoItem[];
  theme_info?: PDFReportThemeInfoItem[];
}

// ALERT DOMAIN TYPES AND HELPERS
export type AlertLeg = {
  action?: string;
  expiration_date?: string;
  strike_price?: number | string | null;
};

export type AlertPayload = {
  contract?: {
    legs?: AlertLeg[];
  };
  [key: string]: unknown;
};

export type AlertType = {
  id: number;
  type?: string | null;
  status?: string | null;
  seen?: boolean;
  time_sent?: string | null;
  order_id?: string | null;
  symbol?: string | null;
  contract_type?: "C" | "P" | string | null;
  net_avg_price?: number | string | null;
  auto_extension_percentage?: number | null;
  auto_partial_close_percentage?: number | null;
  legs?: AlertLeg[];
  payload?: AlertPayload | string | null;
};

export const asText = (value: unknown) =>
  value === null || value === undefined ? "—" : String(value);

export const formatMoney = (value: unknown, decimals = 4) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(decimals)}`;
};

export const contractLabel = (contractType?: string | null) => {
  if (contractType === "C") return "Call";
  if (contractType === "P") return "Put";
  return asText(contractType ?? "");
};

export enum PerformanceReportTypeEnum {
  STOCK = "stock",
  STRATEGY = "strategy",
}

export enum AdjustmentTypeEnum {
  INCEPTION = "INCEPTION",
  BUY = "BUY",
  SELL = "SELL",
  REMOVED = "REMOVED",
  MERGE_IN = "MERGE_IN",
  JOURNAL_IN = "JOURNAL_IN",
  JOURNAL_OUT = "JOURNAL_OUT",
  MAX_TRADE_AMOUNT = "MAX_TRADE_AMOUNT",
}

export enum BalanceTypeEnum {
  TRADE_AMOUNT = "TRADE_AMOUNT",
  PORTFOLIO = "PORTFOLIO",
}

export interface AdjustmentRow {
  id: number;
  adjustment_date: string;
  adjustment_type: AdjustmentTypeEnum;
  adjustment_amount: number;
  unit_balance: number;
  balance_type: BalanceTypeEnum;
  ignore: boolean;
}

export enum OptionTypeEnum {
  CALL = "C",
  PUT = "P",
  CALL_AND_PUT = "CP",
}

export interface AccountHistoryRecord {
  id: number;
  account: number;
  asset: number | null;
  option_type: OptionTypeEnum | null;
  strategy: number | null;
  adjustment_date: string;
  adjustment_type: string;
  adjustment_amount: number;
  unit_balance_after_adjustment: number;
  balance_type: string;
  ignore: boolean;
  created_on?: string;
  created_by?: number | null;
  updated_on?: string;
  updated_by?: number | null;
}

export interface AccountHistoryQueryParams {
  account_id: number;
  asset_id: number;
  option_type?: OptionTypeEnum;
  ordering?: string;
  balance_type?: string;
  adjustment_type?: string;
}

export interface CreateAccountHistoryPayload {
  account: number;
  asset: number;
  option_type?: OptionTypeEnum;
  adjustment_date: string;
  adjustment_type: string;
  adjustment_amount: number;
  balance_type?: string;
  ignore?: boolean;
}

// Map a Strategy's ``call_or_put`` flag to the OptionTypeEnum letter used in
// the asset-based AccountHistory API. Mirrors the backend enum:
//   call_or_put === true  -> PUT
//   call_or_put === false -> CALL
export const optionTypeFromCallOrPut = (
  callOrPut: boolean | null | undefined,
): OptionTypeEnum => (callOrPut ? OptionTypeEnum.PUT : OptionTypeEnum.CALL);

export interface SingleValueCardProps {
  label: string;
  value: number;
  description: string;
  type: SingleValueCardType;
  color: SingleValueCardColor;
  onValueChange: (value: number) => void;
}
export interface MultiValuesCardProps {
  title: string;
  values: ReportValueCardProps[];
  description: string;
  additionalComponents: React.ReactNode | null;
  // Rendered in the title bar, opposite the title (e.g. the Keep card's
  // asset select). Display-only — the PDF payload ignores it.
  headerAction?: React.ReactNode;
}

export interface ReportValueCardProps {
  label: string;
  value: number | string;
  type: SingleValueCardType;
  onValueChange: (value: number | string) => void;
  fullWidthRow?: boolean;
  color?: SingleValueCardColor;
  // Rendered before the label (e.g. the Keep card's tax settings gear on
  // Tax Savings). Display-only — toPdfValues ignores it.
  labelAction?: React.ReactNode;
}

export interface ReportThemedCardConfig {
  label: string;
  description: string;
  values: ReportValueCardProps[];
  addictional_component: PDFReportAddictionalComponent | null;
  headerAction?: React.ReactNode;
}

export interface ReportPdfResponse {
  download_url?: string;
  [key: string]: string[] | unknown;
}

export interface OrionTransactionParams {
  start_date: string;
  end_date: string;
  strategy_id?: number;
  asset_ids?: number[];
  orion_account_id: number;
  report_type: ReportType;
  transaction_type_ids?: number[];
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
}

export interface OrionTransaction {
  id: number;
  status: string;
  transaction_type_id: number;
  transaction_date: string;
  settlement_date: string;
  transaction_amount: number;
  units_amount: number;
  nav_price: number;
  ticker: string;
  transaction_type: string;
  unit_balance: number;
}
